import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Container, Card, Badge, ListGroup } from 'react-bootstrap';

import { selectUser } from '../redux/authSlice';
import {
  fetchCourseByIdWithUnits,
  forkCourseWithUnits,
} from '../services/supabaseClient/courseService';
import { trackSharedCourseView } from '../services/supabaseClient/sessionService';
import { addCourse } from '../redux/coursesSlice';
import FancyButton from '../components/ui/fancyButton';
import LoadingSpinner from '../components/ui/LoadingSpinner';

const CoursePage = () => {
  const { courseId } = useParams();
  const user = useSelector(selectUser);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadCourse = async () => {
      const { data, error } = await fetchCourseByIdWithUnits(courseId);
      if (error || !data) {
        // eslint-disable-next-line no-console
        console.error('Error loading course:', error);
        return navigate('/');
      }

      setCourse(data);
      setLoading(false);

      // Track view if user is logged in
      if (user) {
        await trackSharedCourseView(user.id, courseId);
      }
    };

    loadCourse();
  }, [user, courseId, navigate]);

  const handleAddCourse = async () => {
    if (!user) {
      // Redirect to login with return path
      navigate('/login', { state: { from: `/courses/${courseId}` } });
      return;
    }

    // User is logged in, fork the course
    const { data: newCourse, error } = await forkCourseWithUnits(course);
    if (error) {
      alert('Failed to add course: ' + error.message);
    } else {
      dispatch(addCourse(newCourse));
      alert(`Course "${course.title}" has been added to your dashboard!`);
      navigate('/dashboard');
    }
  };

  if (loading || !course) return <LoadingSpinner />;

  return (
    <Container className="py-5">
      <div className="d-flex justify-content-center">
        <Card style={{ maxWidth: '800px', width: '100%' }} className="shadow">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-start mb-3">
              <div>
                <h2>{course.title}</h2>
                <Badge
                  bg={
                    course.difficulty_level?.toLowerCase() === 'beginner'
                      ? 'success'
                      : course.difficulty_level?.toLowerCase() === 'intermediate'
                        ? 'warning'
                        : 'danger'
                  }
                  className="me-2"
                >
                  {course.difficulty_level || 'Beginner'}
                </Badge>
                <small className="text-muted">{course.units?.length || 0} lessons</small>
              </div>
            </div>

            <p className="mb-3">{course.description}</p>

            {course.objectives && (
              <div className="mb-3">
                <h5>Objectives</h5>
                <p>{course.objectives}</p>
              </div>
            )}

            {course.prior_knowledge && (
              <div className="mb-3">
                <h5>Prior Knowledge</h5>
                <p>{course.prior_knowledge}</p>
              </div>
            )}

            {course.units && course.units.length > 0 && (
              <div className="mb-4">
                <h5>Units</h5>
                <ListGroup>
                  {course.units.map((unit, index) => (
                    <ListGroup.Item key={unit.id || index}>
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <strong>
                            Unit {index + 1}: {unit.title}
                          </strong>
                          {unit.unit_objective && (
                            <p className="mb-0 mt-1 text-muted small">{unit.unit_objective}</p>
                          )}
                        </div>
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </div>
            )}

            <div className="d-flex gap-2 justify-content-end">
              <FancyButton variant="secondary" onClick={() => navigate(-1)}>
                Close
              </FancyButton>
              <FancyButton variant="primary" onClick={handleAddCourse}>
                Add Course
              </FancyButton>
            </div>
          </Card.Body>
        </Card>
      </div>
    </Container>
  );
};

export default CoursePage;
